import { type GenericElement } from '@modules/editor/src/lib/element';

import { type EditorComponent } from '$/stores/useCustomEditorStore';

export const deriveSelectedComponentIds = (
  components: EditorComponent[],
  selectedElementIds: string[],
): string[] => {
  const componentIds = new Set(components.map((c) => c.id));
  return selectedElementIds.filter((id) => componentIds.has(id));
};

export const deriveActiveComponentId = (
  components: EditorComponent[],
  selectedElementIds: string[],
): string => {
  const componentIds = new Set(components.map((c) => c.id));

  for (let i = selectedElementIds.length - 1; i >= 0; i--) {
    const id = selectedElementIds[i];
    if (componentIds.has(id)) return id;

    const parent = components.find((c) => c.elements.includes(id));
    if (parent) return parent.id;
  }
  return '';
};

export const getRangeSelection = (
  orderedIds: string[],
  clickedId: string,
  currentSelected: string[],
): string[] => {
  const clickedIdx = orderedIds.indexOf(clickedId);
  const anchorId = currentSelected.findLast((id) => orderedIds.includes(id));
  const anchorIdx = anchorId ? orderedIds.indexOf(anchorId) : 0;
  const [start, end] = [anchorIdx, clickedIdx].sort((a, b) => a - b);
  const rangeIds = orderedIds.slice(start, end + 1);
  const existing = new Set(currentSelected);

  return [...currentSelected, ...rangeIds.filter((id) => !existing.has(id))];
};

export const isComponentSelection = (
  components: EditorComponent[],
  selectedElementIds: string[],
): boolean => {
  const componentIds = new Set(components.map((c) => c.id));
  return selectedElementIds.some((id) => componentIds.has(id));
};

export const isElementSelection = (
  components: EditorComponent[],
  selectedElementIds: string[],
): boolean => {
  const componentIds = new Set(components.map((c) => c.id));
  return selectedElementIds.some((id) => !componentIds.has(id));
};

export const isUserGeneratedElement = (
  element: GenericElement | undefined,
): element is GenericElement =>
  element != null &&
  element.type !== 'boolean' &&
  element.type !== 'grid' &&
  element.type !== 'colorchecker';
